import {Pill} from '@wandb/components';
import React, {useCallback, useLayoutEffect} from 'react';
import {type Descendant, Editor, Transforms} from 'slate';
import {
  Editable,
  ReactEditor,
  type RenderElementProps,
  Slate,
} from 'slate-react';

import {AGENT_NAME} from '../constants';

export const WBAgentEditor = ({
  editor,
  initialValue,
  onChange,
  onKeyDown,
  onPaste,
  readOnly = false,
  placeholder = `Ask ${AGENT_NAME}`,
}: {
  editor: Editor;
  initialValue: Descendant[];
  onChange: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onPaste?: React.ClipboardEventHandler<HTMLDivElement>;
  readOnly?: boolean;
  placeholder?: string;
}) => {
  const renderElement = useCallback(
    (props: RenderElementProps) => <Element {...props} />,
    []
  );
  useLayoutEffect(() => {
    // NOTE: A hot reload can run this before `Slate` seeds the editor, and
    // focus throws on a root with no children.
    if (!readOnly && editor.children.length > 0) {
      ReactEditor.focus(editor);
      Transforms.select(editor, Editor.end(editor, []));
    }

    // NOTE: The composer memoizes `editor`, so it outlives this mount; drop the
    // caret or the next mount resolves it against its reseeded, shorter body.
    return () => {
      editor.selection = null;
    };
  }, [editor, readOnly]);

  return (
    <Slate editor={editor} initialValue={initialValue} onChange={onChange}>
      <Editable
        renderElement={renderElement}
        placeholder={placeholder}
        spellCheck
        autoFocus={!readOnly}
        readOnly={readOnly}
        onKeyDown={onKeyDown}
        onPaste={onPaste}
        className={`outline-none max-h-[180px] overflow-y-auto overscroll-none [scrollbar-width:thin] [scrollbar-color:#0d0f1252_transparent]${readOnly ? ' cursor-not-allowed opacity-50' : ''}`}
      />
    </Slate>
  );
};

const Element = ({attributes, children, element}: RenderElementProps) => {
  if (element.type === 'run-mention') {
    return (
      <span {...attributes} contentEditable={false} className="align-middle">
        <Pill icon="running-repeat" textValue={element.displayName} />
        <span style={{display: 'none'}}>{children}</span>
      </span>
    );
  }
  return <div {...attributes}>{children}</div>;
};
